import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, BarChart2, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './LiquidityRatioCalculatorPage/components/CalculationForm';
import ResultsOverview from './LiquidityRatioCalculatorPage/components/ResultsOverview';
import LiquidityVisualization from './LiquidityRatioCalculatorPage/components/LiquidityVisualization';
import QuickAdjustmentSidebar from './LiquidityRatioCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './LiquidityRatioCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './LiquidityRatioCalculatorPage/components/TipsSidebar';
import InfoContent from './LiquidityRatioCalculatorPage/components/InfoContent';
import IndustryStandardsTable from './LiquidityRatioCalculatorPage/components/IndustryStandardsTable';
import LiquidityTrendAnalysis from './LiquidityRatioCalculatorPage/components/LiquidityTrendAnalysis';

// Определение начального состояния формы
export const initialFormData = {
  // Оборотные активы
  currentAssets: {
    cash: '100000', // Денежные средства
    shortTermInvestments: '50000', // Краткосрочные финансовые вложения
    accountsReceivable: '200000', // Дебиторская задолженность
    inventory: '300000', // Запасы
    vatReceivable: '20000', // НДС по приобретенным ценностям
    otherCurrentAssets: '30000', // Прочие оборотные активы
  },
  
  // Краткосрочные обязательства
  currentLiabilities: {
    shortTermLoans: '150000', // Краткосрочные заемные средства
    accountsPayable: '180000', // Кредиторская задолженность
    advancesReceived: '50000', // Авансы полученные
    taxesPayable: '40000', // Задолженность по налогам
    otherCurrentLiabilities: '30000', // Прочие краткосрочные обязательства
  },
  
  // Долгосрочные активы (для расчета общего коэффициента ликвидности)
  longTermAssets: {
    nonCurrentAssets: '800000', // Внеоборотные активы
  },
  
  // Долгосрочные обязательства (для расчета общего коэффициента ликвидности)
  longTermLiabilities: {
    longTermLoans: '300000', // Долгосрочные заемные средства
    otherLongTermLiabilities: '100000', // Прочие долгосрочные обязательства
  },
  
  // Дополнительные настройки
  settings: {
    industry: 'manufacturing', // Отрасль (для сравнения с нормативами)
    companySize: 'medium', // Размер компании
    accountingStandard: 'ras', // Стандарт отчетности (RAS - РСБУ, IFRS - МСФО)
    includeDetailedAssets: true, // Включить детализацию активов
    includeDetailedLiabilities: true, // Включить детализацию обязательств
    includeBankruptcyRiskAssessment: true, // Включить оценку риска банкротства
    comparisonPeriod: 'current', // Период для сравнения (current, previous, custom)
    customPeriodName: 'Плановый период', // Название периода
  }
};

// Определение отраслей для сравнения с нормативами
export const industryTypes = [
  { id: 'manufacturing', name: 'Производство', currentRatioMin: 1.5, quickRatioMin: 1.0, cashRatioMin: 0.2 },
  { id: 'retail', name: 'Розничная торговля', currentRatioMin: 1.2, quickRatioMin: 0.8, cashRatioMin: 0.1 },
  { id: 'wholesale', name: 'Оптовая торговля', currentRatioMin: 1.3, quickRatioMin: 0.9, cashRatioMin: 0.15 },
  { id: 'construction', name: 'Строительство', currentRatioMin: 1.7, quickRatioMin: 1.2, cashRatioMin: 0.25 },
  { id: 'services', name: 'Сфера услуг', currentRatioMin: 1.1, quickRatioMin: 0.7, cashRatioMin: 0.1 },
  { id: 'agriculture', name: 'Сельское хозяйство', currentRatioMin: 1.8, quickRatioMin: 1.0, cashRatioMin: 0.2 },
  { id: 'it', name: 'Информационные технологии', currentRatioMin: 1.3, quickRatioMin: 1.1, cashRatioMin: 0.5 },
  { id: 'energy', name: 'Энергетика', currentRatioMin: 1.5, quickRatioMin: 1.0, cashRatioMin: 0.2 },
  { id: 'mining', name: 'Добыча полезных ископаемых', currentRatioMin: 1.6, quickRatioMin: 1.1, cashRatioMin: 0.3 },
  { id: 'transport', name: 'Транспорт и логистика', currentRatioMin: 1.4, quickRatioMin: 0.9, cashRatioMin: 0.15 },
];

// Определение размеров компаний
export const companySizes = [
  { id: 'micro', name: 'Микропредприятие', revenueLimit: '120 млн ₽', employeesLimit: '15 чел.' },
  { id: 'small', name: 'Малое предприятие', revenueLimit: '800 млн ₽', employeesLimit: '100 чел.' },
  { id: 'medium', name: 'Среднее предприятие', revenueLimit: '2 млрд ₽', employeesLimit: '250 чел.' },
  { id: 'large', name: 'Крупное предприятие', revenueLimit: 'свыше 2 млрд ₽', employeesLimit: 'свыше 250 чел.' },
];

/**
 * LiquidityRatioCalculatorPage Component - Полный калькулятор коэффициентов ликвидности с возможностью детализации и сравнения с отраслевыми нормативами.
 */
const LiquidityRatioCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('liquidity-ratio-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор коэффициентов ликвидности | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор коэффициентов ликвидности предприятия: текущей, быстрой, абсолютной ликвидности с анализом и сравнением с отраслевыми нормативами в соответствии с РСБУ и МСФО.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [showTrends, setShowTrends] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных отчетов из localStorage ---
  useEffect(() => {
    const savedReportsData = localStorage.getItem('savedLiquidityReports');
    if (savedReportsData) {
      try {
        setSavedReports(JSON.parse(savedReportsData));
      } catch (e) {
        console.error("Error loading saved reports:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'current-ratio':
          newState.settings.includeDetailedAssets = true;
          newState.settings.includeDetailedLiabilities = true;
          break;
        case 'quick-ratio':
          newState.settings.includeDetailedAssets = true;
          newState.settings.includeDetailedLiabilities = false;
          break;
        case 'cash-ratio':
          newState.settings.includeDetailedAssets = true;
          newState.settings.includeDetailedLiabilities = false;
          break;
        case 'bankruptcy-analysis':
          newState.settings.includeBankruptcyRiskAssessment = true;
          break;
        case 'industry-comparison':
          newState.settings.industry = 'manufacturing';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});
    setShowTrends(false);

  }, [subToolId]);

  // Сохранение отчета в localStorage
  const saveReport = (name) => {
    if (!results) return;

    const newSavedReport = {
      id: Date.now().toString(),
      name: name || `Отчет о ликвидности от ${new Date().toLocaleDateString()}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      industry: formData.settings.industry
    };

    const updatedSavedReports = [...savedReports, newSavedReport];
    setSavedReports(updatedSavedReports);
    localStorage.setItem('savedLiquidityReports', JSON.stringify(updatedSavedReports));

    return newSavedReport;
  };

  // Удаление сохраненного отчета
  const deleteSavedReport = (id) => {
    const updatedSavedReports = savedReports.filter(report => report.id !== id);
    setSavedReports(updatedSavedReports);
    localStorage.setItem('savedLiquidityReports', JSON.stringify(updatedSavedReports));
  };

  // Загрузка сохраненного отчета
  const loadSavedReport = (id) => {
    const reportToLoad = savedReports.find(report => report.id === id);
    if (reportToLoad) {
      setFormData(reportToLoad.formData);
      setResults(reportToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор коэффициентов ликвидности'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              industryTypes={industryTypes}
              companySizes={companySizes}
              saveReport={saveReport}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} industryTypes={industryTypes} formData={formData} />
                <LiquidityVisualization results={results} />
                <IndustryStandardsTable industry={formData.settings.industry} industryTypes={industryTypes} results={results} />
                
                {showTrends && savedReports.length > 1 && (
                  <LiquidityTrendAnalysis 
                    currentResults={results} 
                    historicalData={savedReports} 
                  />
                )}
                
                {savedReports.length > 1 && !showTrends && (
                  <div className="mb-8 text-center">
                    <button 
                      onClick={() => setShowTrends(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <BarChart2 className="mr-2 h-5 w-5" />
                      Показать динамику коэффициентов ликвидности
                    </button>
                  </div>
                )}
              </>
            )}

            {/* --- Информационный контент о ликвидности --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              industryTypes={industryTypes}
            />

            {/* --- Панель сравнения отчетов --- */}
            <ComparisonSidebar 
              savedReports={savedReports}
              deleteSavedReport={deleteSavedReport}
              loadSavedReport={loadSavedReport}
              industryTypes={industryTypes}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default LiquidityRatioCalculatorPage;